"use client";

import { useEffect, useRef } from "react";
import { mutate } from "swr";
import { persistor } from "@/app/redux/store";

const VERSION_URL = "/api/version";
const POLL_INTERVAL = 60_000;

async function fetchVersion(): Promise<string | null> {
	try {
		const res = await fetch(VERSION_URL, { cache: "no-store" });
		if (!res.ok) return null;
		return (await res.text()).trim();
	} catch {
		return null;
	}
}

async function applyUpdate() {
	try {
		await mutate(() => true, undefined, { revalidate: false });
	} catch (error) {
		console.error("SWR purge failed:", error);
	}
	try {
		persistor.pause();
		await persistor.flush();
		await persistor.purge();
	} catch (error) {
		console.error("redux-persist purge failed:", error);
	}
	try {
		if (typeof window !== "undefined") {
			window.localStorage.clear();
			window.sessionStorage.clear();
		}
	} catch (error) {
		console.error("Storage clear failed:", error);
	}
	window.location.reload();
}

export default function VersionPoller() {
	const initialVersion = useRef<string | null>(null);
	const reloading = useRef(false);

	useEffect(() => {
		let cancelled = false;

		const check = async () => {
			if (reloading.current) return;
			const v = await fetchVersion();
			if (cancelled || !v) return;
			if (initialVersion.current === null) {
				initialVersion.current = v;
				return;
			}
			if (v !== initialVersion.current) {
				reloading.current = true;
				await applyUpdate();
			}
		};

		check();
		const id = setInterval(check, POLL_INTERVAL);
		const onFocus = () => check();
		window.addEventListener("focus", onFocus);

		return () => {
			cancelled = true;
			clearInterval(id);
			window.removeEventListener("focus", onFocus);
		};
	}, []);

	return null;
}
